import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, Alert } from '@mui/material';
import { Icon } from '@iconify/react';
import { EditIcon, XIcon, SendIcon } from 'lucide-react';
import { createAuditLog } from '../../utils/auditLog';
interface RejectedTransaction {
  id: string;
  amount: number;
  currency: string;
  payoutAmount: number;
  payoutCurrency: string;
  senderName: string;
  receiverName: string;
  rejectionReason: string;
  rejectedAt: string;
  rejectedBy: string;
}
const RejectedTransactionsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<RejectedTransaction[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<RejectedTransaction | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editedData, setEditedData] = useState<any>({});
  useEffect(() => {
    loadRejectedTransactions();
  }, []);
  const loadRejectedTransactions = () => {
    const stored = localStorage.getItem('initiatedTransactions');
    if (stored) {
      const allTransactions = JSON.parse(stored);
      const rejected = allTransactions.filter((t: any) => t.checkerDecision === 'rejected');
      setTransactions(rejected);
    }
  };
  const handleEdit = (transaction: RejectedTransaction) => {
    setSelectedTransaction(transaction);
    setEditedData({
      amount: transaction.amount,
      receiverName: transaction.receiverName,
      senderName: transaction.senderName
    });
    setShowEditDialog(true);
  };
  const handleResubmit = () => {
    if (!selectedTransaction) return;
    const stored = localStorage.getItem('initiatedTransactions');
    if (!stored) return;
    const allTransactions = JSON.parse(stored);
    const updatedTransactions = allTransactions.map((t: any) => {
      if (t.id === selectedTransaction.id) {
        return {
          ...t,
          ...editedData,
          status: 'Pending Checker Approval',
          checkerDecision: null,
          checkerReason: null,
          resubmittedAt: new Date().toISOString()
        };
      }
      return t;
    });
    localStorage.setItem('initiatedTransactions', JSON.stringify(updatedTransactions));
    createAuditLog({
      userId: selectedTransaction.id,
      eventType: 'Edit',
      source: 'BO_USER',
      changes: [{
        fieldName: 'Transaction Status',
        oldValue: 'Rejected',
        newValue: 'Pending Checker Approval (Resubmitted)'
      }]
    });
    window.dispatchEvent(new Event('transactionUpdated'));
    setShowEditDialog(false);
    loadRejectedTransactions();
  };
  return <Box sx={{
    p: 3
  }} data-id="element-1049">
      <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      mb: 3
    }} data-id="element-1050">
        <Icon icon="lucide:x-circle" width={28} height={28} color="#AE2A19" data-id="element-1051" />
        <Typography variant="h4" sx={{
        fontWeight: 700,
        color: '#AE2A19'
      }} data-id="element-1052">
          Rejected Transactions
        </Typography>
      </Box>

      <Alert severity="info" sx={{
      mb: 3
    }} data-id="element-1053">
        These transactions were rejected by the checker. You can edit and
        resubmit them for approval.
      </Alert>

      <TableContainer component={Paper} elevation={0} sx={{
      borderRadius: 3
    }} data-id="element-1054">
        <Table data-id="element-1055">
          <TableHead data-id="element-1056">
            <TableRow sx={{
            bgcolor: 'error.50'
          }} data-id="element-1057">
              <TableCell sx={{
              fontWeight: 600
            }} data-id="element-1058">
                Transaction ID
              </TableCell>
              <TableCell sx={{
              fontWeight: 600
            }} data-id="element-1059">
                Sender
              </TableCell>
              <TableCell sx={{
              fontWeight: 600
            }} data-id="element-1060">
                Receiver
              </TableCell>
              <TableCell sx={{
              fontWeight: 600
            }} data-id="element-1061">
                Amount
              </TableCell>
              <TableCell sx={{
              fontWeight: 600
            }} data-id="element-1062">
                Rejection Reason
              </TableCell>
              <TableCell sx={{
              fontWeight: 600
            }} data-id="element-1063">
                Rejected At
              </TableCell>
              <TableCell sx={{
              fontWeight: 600
            }} data-id="element-1064">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-id="element-1065">
            {transactions.length === 0 ? <TableRow data-id="element-1066">
                <TableCell colSpan={7} align="center" sx={{
              py: 4
            }} data-id="element-1067">
                  <Typography color="text.secondary" data-id="element-1068">
                    No rejected transactions found
                  </Typography>
                </TableCell>
              </TableRow> : transactions.map(transaction => <TableRow key={transaction.id} hover data-id="element-1069">
                  <TableCell data-id="element-1070">{transaction.id}</TableCell>
                  <TableCell data-id="element-1071">{transaction.senderName}</TableCell>
                  <TableCell data-id="element-1072">{transaction.receiverName}</TableCell>
                  <TableCell data-id="element-1073">
                    {transaction.amount} {transaction.currency}
                  </TableCell>
                  <TableCell data-id="element-1074">
                    <Typography variant="body2" color="error" data-id="element-1075">
                      {transaction.rejectionReason}
                    </Typography>
                  </TableCell>
                  <TableCell data-id="element-1076">
                    {new Date(transaction.rejectedAt).toLocaleString()}
                  </TableCell>
                  <TableCell data-id="element-1077">
                    <IconButton size="small" onClick={() => handleEdit(transaction)} sx={{
                color: '#0052CC'
              }} data-id="element-1078">
                      <EditIcon size={18} data-id="element-1079" />
                    </IconButton>
                  </TableCell>
                </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="md" fullWidth data-id="element-1080">
        <DialogTitle data-id="element-1081">
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }} data-id="element-1082">
            <Typography variant="h6" data-id="element-1083">Edit & Resubmit Transaction</Typography>
            <IconButton onClick={() => setShowEditDialog(false)} data-id="element-1084">
              <XIcon size={20} data-id="element-1085" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-1086">
          {selectedTransaction && <>
              <Alert severity="warning" sx={{
            mb: 3
          }} data-id="element-1087">
                <Typography variant="body2" fontWeight={600} data-id="element-1088">
                  Rejection Reason:
                </Typography>
                <Typography variant="body2" data-id="element-1089">
                  {selectedTransaction.rejectionReason}
                </Typography>
              </Alert>

              <Grid container spacing={2} data-id="element-1090">
                <Grid item xs={12} md={6} data-id="element-1091">
                  <TextField fullWidth label="Sender Name" value={editedData.senderName} onChange={e => setEditedData({
                ...editedData,
                senderName: e.target.value
              })} data-id="element-1092" />
                </Grid>
                <Grid item xs={12} md={6} data-id="element-1093">
                  <TextField fullWidth label="Receiver Name" value={editedData.receiverName} onChange={e => setEditedData({
                ...editedData,
                receiverName: e.target.value
              })} data-id="element-1094" />
                </Grid>
                <Grid item xs={12} md={6} data-id="element-1095">
                  <TextField fullWidth label="Amount" type="number" value={editedData.amount} onChange={e => setEditedData({
                ...editedData,
                amount: e.target.value
              })} data-id="element-1096" />
                </Grid>
              </Grid>
            </>}
        </DialogContent>
        <DialogActions data-id="element-1097">
          <Button onClick={() => setShowEditDialog(false)} data-id="element-1098">Cancel</Button>
          <Button variant="contained" startIcon={<SendIcon size={18} data-id="element-1100" />} onClick={handleResubmit} data-id="element-1099">
            Resubmit for Approval
          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
};
export default RejectedTransactionsPage;